import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { Tenant } from '@/types/database';

interface TenantFilterContextType {
  tenants: Tenant[];
  selectedTenantIds: string[];
  setSelectedTenantIds: (ids: string[]) => void;
  loading: boolean;
  isFiltered: boolean;
  // Helper to check if a tenant_id matches the current filter
  matchesFilter: (tenantId: string | null | undefined) => boolean;
  // Consultant multi-tenant support
  isConsultor: boolean;
  consultorActiveTenantId: string | null;
  setConsultorActiveTenant: (tenantId: string) => void;
}

const TenantFilterContext = createContext<TenantFilterContextType | undefined>(undefined);

export function TenantFilterProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [consultorActiveTenantId, setConsultorActiveTenantId] = useState<string | null>(null);
  const { isSuperAdmin, profile, loading: authLoading } = useAuth();

  const isConsultor = profile?.role === 'consultor';

  useEffect(() => {
    async function fetchTenants() {
      // Wait for auth to finish loading AND profile to be available
      if (authLoading) {
        return;
      }

      // If user is not logged in (no profile), don't fetch
      if (!profile) {
        setLoading(false);
        return;
      }

      try {
        // Fetch tenants - RLS will filter based on user role:
        // - Super Admins: get all tenants
        // - Consultants with multi-tenant: get own tenant + assigned tenants
        // - Others: get only their own tenant
        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .order('name');

        if (error) throw error;
        setTenants(data || []);
        // Default: select all available tenants
        setSelectedTenantIds((data || []).map(t => t.id));

        // For consultants: load active tenant from user_active_tenant or default to profile tenant
        if (profile.role === 'consultor' && data && data.length > 0) {
          const { data: activeTenantData } = await supabase
            .from('user_active_tenant')
            .select('tenant_id')
            .eq('user_id', profile.id)
            .maybeSingle();

          if (activeTenantData && data.some(t => t.id === activeTenantData.tenant_id)) {
            setConsultorActiveTenantId(activeTenantData.tenant_id);
          } else {
            // Default to profile's tenant_id or first available
            setConsultorActiveTenantId(profile.tenant_id || data[0]?.id || null);
          }
        }

        setHasFetched(true);
      } catch (error) {
        console.error('Error fetching tenants:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchTenants();
  }, [authLoading, profile]);

  // Check if filtering is active (not all tenants selected)
  const isFiltered = isSuperAdmin && selectedTenantIds.length < tenants.length && selectedTenantIds.length > 0;

  // Helper function to check if a tenant_id matches the current filter
  const matchesFilter = (tenantId: string | null | undefined): boolean => {
    if (!isSuperAdmin) return true; // Non-super admins always match (RLS handles filtering)
    if (selectedTenantIds.length === 0) return true; // No filter = show all
    if (selectedTenantIds.length === tenants.length) return true; // All selected = show all
    if (!tenantId) return false;
    return selectedTenantIds.includes(tenantId);
  };

  // Switch active tenant for consultant (persists to DB)
  const setConsultorActiveTenant = async (tenantId: string) => {
    if (!profile || !isConsultor) return;

    setConsultorActiveTenantId(tenantId);

    try {
      // Upsert active tenant
      const { error } = await supabase
        .from('user_active_tenant')
        .upsert(
          { user_id: profile.id, tenant_id: tenantId, updated_at: new Date().toISOString() },
          { onConflict: 'user_id' }
        );

      if (error) {
        console.error('Error setting active tenant:', error);
      }

      // Reload page to re-trigger RLS with new active tenant
      window.location.reload();
    } catch (error) {
      console.error('Error setting active tenant:', error);
    }
  };

  return (
    <TenantFilterContext.Provider 
      value={{ 
        tenants, 
        selectedTenantIds, 
        setSelectedTenantIds, 
        loading,
        isFiltered,
        matchesFilter,
        isConsultor,
        consultorActiveTenantId,
        setConsultorActiveTenant,
      }}
    >
      {children}
    </TenantFilterContext.Provider>
  );
}

export function useTenantFilter() {
  const context = useContext(TenantFilterContext);
  if (context === undefined) {
    throw new Error('useTenantFilter must be used within a TenantFilterProvider');
  }
  return context;
}